import { useState, useCallback } from 'react';
import { Notification } from '../components/GlobalNotification';

export type NotificationContextType = {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  showSuccess: (title: string, message: string, persistent?: boolean) => string;
  showError: (title: string, message: string, persistent?: boolean) => string;
  showWarning: (title: string, message: string, persistent?: boolean) => string;
  showInfo: (title: string, message: string, persistent?: boolean) => string;
};

export function useNotification(): NotificationContextType {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date(),
    };
    
    setNotifications(prev => [...prev, newNotification]);
    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Convenience methods
  const showSuccess = useCallback((title: string, message: string, persistent = false) => {
    return addNotification({ type: 'success', title, message, persistent });
  }, [addNotification]);

  const showError = useCallback((title: string, message: string, persistent = true) => {
    return addNotification({ type: 'error', title, message, persistent });
  }, [addNotification]);

  const showWarning = useCallback((title: string, message: string, persistent = false) => {
    return addNotification({ type: 'warning', title, message, persistent });
  }, [addNotification]);

  const showInfo = useCallback((title: string, message: string, persistent = false) => {
    return addNotification({ type: 'info', title, message, persistent });
  }, [addNotification]);

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
}
